import React, { FC, Fragment, useEffect } from 'react';
import { FormControlLabel, Tabs, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import LinkIcon from '@mui/icons-material/Link';
import SubjectRoundedIcon from '@mui/icons-material/SubjectRounded';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useTranslation } from 'react-i18next';

import { ContentInput, GroupContentApprovalType, UserFeedFollowGroupFragment } from 'api';
import { useCapabilities, useFollowed, useNetworkCapabilities, useProtectedClient } from 'hooks';
import { Capability } from 'components';
import { ComposeLayout, ComposeTab, ComposeTabBody } from 'features/compose';

import {
  ContentTagging,
  Disclosures,
  FormButtons,
  GroupSelection,
  ImageVideoForm,
  LinkForm,
  MarkImportant,
  MicrosoftTeams,
  PinPost,
  ScheduleExpirePost,
  ShareCopyWidget,
  SingleGroupDropdown,
  Slack,
  TextForm,
  UrlTracking,
} from '../components';
import { usePostContext } from '../PostContext';
import { PostOption } from '../types';
import { Switch } from 'features/posting/components';
import { ContentProvider } from 'providers';
import { UploadCountProvider } from '../components/ImageVideo/UploadCountProvider';
import useReleases from 'hooks/useReleases';

import theme from 'theme';

const styles = {
  tabIcon: {
    marginRight: theme.spacing(),
  },
  subtitle: {
    margin: theme.spacing(4, 0, 0, 0),
    padding: 0,
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  switch: {
    top: 0,
    right: 0,
    marginLeft: 0,
  },
  labelPlacementStart: {
    marginLeft: 0,
  },
  activeTab: {
    '&.Mui-selected': {
      color: 'primary.main',
    },
  },
} as const;

const Post: FC = ({ children }) => {
  const { values, setFieldValue } = useFormikContext<ContentInput>();
  const { t } = useTranslation();
  const { isAdmin, isModerator, isContributor } = useCapabilities();
  const { hasSlack, hasMSTeams } = useNetworkCapabilities();
  const { moderator_can_mark_as_important } = useProtectedClient();
  const releases = useReleases();
  const { state, dispatch } = usePostContext();
  const { owned, moderated } = useFollowed();

  const has4EyeModeration = releases.includes('4EyeModeration');
  const hasModeratorWidgets = isAdmin || (isModerator && state.moderatorMode);
  const hasContributorWidgets = hasModeratorWidgets || isContributor;
  const hasMarkImportant = isAdmin || (hasModeratorWidgets && moderator_can_mark_as_important);
  const hasContentTagging = releases.includes('UseContentTagging');
  const group = [...moderated, ...owned].find(
    (group: UserFeedFollowGroupFragment) => group.id === values.primary_group_id
  );
  const isEdit = window.location.href.includes('/post/edit/');
  const is4EyeModerated = group?.content_approval_type === GroupContentApprovalType.FourEyes;
  const disabled = has4EyeModeration && isEdit && is4EyeModerated;

  useEffect(() => {
    if (isEdit) {
      dispatch({ type: 'editing', value: isEdit });
    }
  }, []);

  function handleChangeTab(event: unknown, value: PostOption) {
    dispatch({ type: 'option', value });
    dispatch({ type: 'slack', value: false });
    dispatch({ type: 'msteams', value: false });
    setFieldValue('slack_channels', []);
    setFieldValue('ms_teams_channels', []);

    setFieldValue('content_object.media', null);
    setFieldValue('content_object.url', '');
    setFieldValue('content_object.photos', []);

    setFieldValue('content_object.url_metadata_override.title', '');
    setFieldValue('content_object.url_metadata_override.description', '');
    setFieldValue('url_param_values', []);

    dispatch({ type: 'validUrl', value: false });
    dispatch({ type: 'clearPhotoIndexes' });
    if (values.content_object.photos.length) {
      dispatch({ type: 'updatePhotoIndexes', value: 0 });
    }
  }

  function renderTabs() {
    return (
      <Tabs
        value={state.option}
        onChange={handleChangeTab}
        textColor="primary"
        indicatorColor="primary"
        variant="fullWidth"
      >
        <ComposeTab
          value={PostOption.Link}
          disabled={isEdit && state.option !== PostOption.Link}
          icon={<LinkIcon />}
          iconPosition="start"
          label={t('common:link')}
          sx={styles.activeTab}
        />
        <ComposeTab
          value={PostOption.ImageVideo}
          disabled={isEdit && state.option !== PostOption.ImageVideo}
          icon={<AddPhotoAlternateIcon />}
          iconPosition="start"
          label={t('posting:imageOrVideo')}
          sx={styles.activeTab}
        />
        <ComposeTab
          value={PostOption.Text}
          disabled={isEdit && state.option !== PostOption.Text}
          icon={<SubjectRoundedIcon />}
          iconPosition="start"
          label={t('common:Text')}
          sx={styles.activeTab}
        />
      </Tabs>
    );
  }

  function renderPrimaryGroupDropDown() {
    const preserveLegacyBehaviour = isEdit && !state.requiresApproval;

    return <SingleGroupDropdown disabled={preserveLegacyBehaviour} />;
  }

  function handleToggleRepost() {
    dispatch({ type: 'repost', value: !state.repost });
    dispatch({ type: 'moderatorMode', value: !state.repost });
  }

  function renderHeader() {
    const showMultiGroupHeader =
      values.primary_group_id &&
      (state.repost || isAdmin) &&
      !(has4EyeModeration && is4EyeModerated);

    return (
      <Fragment>
        <Grid sx={styles.title}>
          <Typography variant="h4" display="span">
            {isEdit ? t('posting:editPost') : t('posting:createPost')}
          </Typography>

          {isModerator && !isAdmin && !(has4EyeModeration && is4EyeModerated) && (
            <FormControlLabel
              value={state.repost}
              label={t('posting:toggleRepost')}
              labelPlacement="start"
              sx={styles.labelPlacementStart}
              control={
                <Switch
                  data-testid="post-radio-toggleRepost"
                  sx={{ root: styles.switch }}
                  checked={state.repost}
                  color="primary"
                  onChange={handleToggleRepost}
                />
              }
            />
          )}
        </Grid>

        <Grid container alignItems="center">
          <Grid xs={5}>{renderPrimaryGroupDropDown()}</Grid>
          {showMultiGroupHeader && (
            <Capability name="Post.Editing.Function.Edit">
              <Grid xs={2}>
                <Typography variant="body2" align="center">
                  {`${t('posting:repostTo')}:`}
                </Typography>
              </Grid>
              <Grid xs={5}>
                <GroupSelection />
              </Grid>
            </Capability>
          )}
        </Grid>

        <Grid sx={styles.subtitle}>
          <Typography variant="subtitle1">{t('posting:createPostSubhead')}</Typography>
        </Grid>
      </Fragment>
    );
  }

  function renderBody() {
    return (
      <ComposeTabBody>
        {state.option === PostOption.Link && (
          <LinkForm
            contributor={hasContributorWidgets}
            moderator={hasModeratorWidgets}
            disabled={disabled}
          />
        )}
        {state.option === PostOption.ImageVideo && (
          <ImageVideoForm
            contributor={hasContributorWidgets}
            moderator={hasModeratorWidgets}
            disabled={disabled}
          />
        )}
        {state.option === PostOption.Text && (
          <TextForm contributor={hasContributorWidgets} moderator={hasModeratorWidgets} />
        )}
      </ComposeTabBody>
    );
  }

  return (
    <UploadCountProvider>
      <ComposeLayout header={renderHeader()} tabs={renderTabs()} body={renderBody()}>
        <Grid container spacing={2}>
          {hasContributorWidgets && (
            <Grid xs={12}>
              <ContentProvider value={values}>
                <ShareCopyWidget disabled={disabled} />
              </ContentProvider>
            </Grid>
          )}

          {hasMarkImportant && (
            <Grid xs={12}>
              <MarkImportant />
            </Grid>
          )}

          {hasModeratorWidgets && (
            <Grid xs={12}>
              <PinPost />
            </Grid>
          )}

          {hasModeratorWidgets && (
            <Grid xs={12}>
              <Capability name="Post.Create.Function.ScheduleExpire">
                <ScheduleExpirePost />
              </Capability>
            </Grid>
          )}

          {hasModeratorWidgets && (
            <Grid xs={12}>
              <Capability name="Post.Create.Function.ApplyDisclosures">
                <Disclosures disabled={disabled} />
              </Capability>
            </Grid>
          )}

          {hasContentTagging && (
            <Grid xs={12}>
              <ContentTagging />
            </Grid>
          )}

          {state.option === PostOption.Link && hasModeratorWidgets && (
            //Moved the Grid inside child component so its not rendered unless the widget is shown
            <Capability name="Post.Create.Function.ApplyUtmParameters">
              <UrlTracking />
            </Capability>
          )}

          {state.option !== PostOption.Text && hasMSTeams && (
            <Grid xs={12}>
              <MicrosoftTeams />
            </Grid>
          )}

          {state.option !== PostOption.Text && hasSlack && (
            <Grid xs={12}>
              <Slack />
            </Grid>
          )}

          <Grid xs={12}>{children}</Grid>

          <Grid xs={12}>
            <FormButtons />
          </Grid>
        </Grid>
      </ComposeLayout>
    </UploadCountProvider>
  );
};

export default Post;
